import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  getPagedLoginLogs,
  filterLoginLogsByDateRange,
} from '../../../../services/LoginLogService';
import { useLanguage } from '../../../../context/LanguageContext';
import { normalizeSupportedLanguage } from '../../../../utils/createScopedI18n';
import { loginLogI18n } from './i18n';

function countryToFlagEmoji(code = '') {
  return code
    .toUpperCase()
    .replace(/./g, (character) =>
      String.fromCodePoint(127397 + character.charCodeAt(0))
    );
}

const localeByLanguage = {
  esCL: 'es-CL',
  enUS: 'en-US',
  ptBR: 'pt-BR',
};

export default function LoginLog() {
  const { language } = useLanguage();
  const loginLogLanguage = normalizeSupportedLanguage(language);
  const { t } = useTranslation(undefined, { i18n: loginLogI18n });
  const { DesignSystemComponents } = useOutletContext() ?? {};
  const { Card, Button, Input, Table, Badge } = DesignSystemComponents ?? {};

  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  const [tableKey, setTableKey] = useState(0);
  const [sortKey, setSortKey] = useState(null);
  const [sortDirection, setSortDirection] = useState(null);

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isFiltered, setIsFiltered] = useState(false);

  useEffect(() => {
    loginLogI18n.changeLanguage(loginLogLanguage);
  }, [loginLogLanguage]);

  const locale = localeByLanguage[loginLogLanguage] || 'es-CL';

  const mapSortKeyToBackend = (key) => {
    switch (key) {
      case 'date':
        return 'date';
      case 'user':
        return 'user';
      case 'action':
        return 'action';
      default:
        return null;
    }
  };

  const handleServerSortChange = ({ sortKey: key, direction }) => {
    const normalizedDirection =
      typeof direction === 'string' ? direction.toLowerCase() : direction;
    setSortKey(normalizedDirection ? key : null);
    setSortDirection(normalizedDirection ?? null);
    setPage(0);
  };

  const backendSortKey = mapSortKeyToBackend(sortKey) || 'date';
  const backendSortDesc =
    sortDirection === null ? true : sortDirection === 'desc';

  const fetchLoginLogs = async () => {
    try {
      setLoading(true);
      let response;

      if (isFiltered && (startDate || endDate)) {
        const sd = startDate ? `${startDate}T00:00:00` : '1900-01-01T00:00:00';
        const ed = endDate ? `${endDate}T23:59:59` : '2100-12-31T23:59:59';
        response = await filterLoginLogsByDateRange(
          sd,
          ed,
          page + 1,
          pageSize,
          backendSortKey,
          backendSortDesc
        );
      } else {
        response = await getPagedLoginLogs(
          page + 1,
          pageSize,
          backendSortKey,
          backendSortDesc
        );
      }

      setRows(response.data);
      setTotal(response.total);
    } catch (error) {
      console.error('Error loading login logs:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLoginLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, isFiltered, pageSize, backendSortKey, backendSortDesc]);

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPage(0);
    setTableKey((prev) => prev + 1);
  };

  const onFilter = () => {
    setPage(0);
    setIsFiltered(true);
  };

  const onClearFilter = () => {
    setStartDate('');
    setEndDate('');
    setPage(0);
    setIsFiltered(false);
  };

  if (!Card || !Button || !Input || !Table || !Badge) return null;

  return (
    <Card specialPadding='p-4'>
      <div className='mb-3'>
        <h2 className='mb-1'>{t('login_log_title')}</h2>
        <p className='caption font-light'>{t('login_log_subtitle')}</p>
      </div>

      <div className='d-flex align-items-center gap-3 mb-3 flex-wrap'>
        <div style={{ minWidth: 220 }}>
          <label className='caption d-block mb-1'>
            {t('login_log_filter_start_date')}
          </label>
          <Input
            type='date'
            name='loginStart'
            value={startDate}
            onChange={(event) => setStartDate(event.target.value)}
          />
        </div>
        <div style={{ minWidth: 220 }}>
          <label className='caption d-block mb-1'>
            {t('login_log_filter_end_date')}
          </label>
          <Input
            type='date'
            name='loginEnd'
            value={endDate}
            onChange={(event) => setEndDate(event.target.value)}
          />
        </div>
        <div style={{ marginTop: '0px' }}>
          <Button
            label={t('login_log_filter_button')}
            size='sm'
            variant='primary'
            onClick={onFilter}
          />
        </div>
        {isFiltered && (
          <div style={{ marginTop: '0px' }}>
            <Button
              label={t('login_log_clear_button')}
              size='sm'
              variant='secondary'
              onClick={onClearFilter}
            />
          </div>
        )}
      </div>

      <div className='table-container' style={{ fontSize: '13px' }}>
        <style>{`
          .table-container table {
            font-size: 13px !important;
          }
          .table-container th,
          .table-container td {
            font-size: 13px !important;
            padding: 8px 12px !important;
          }
        `}</style>
        <Table
          key={`table-${tableKey}-${pageSize}`}
          columns={[
            {
              accessor: 'date',
              label: t('login_log_col_date'),
              sort: true,
              render: (row) => new Date(row.date).toLocaleString(locale),
            },
            { accessor: 'user', label: t('login_log_col_user'), sort: true },
            {
              label: t('login_log_col_ip'),
              render: (row) => {
                const country = row.country || '';
                const ip = row.ipAddress || '';
                const flag =
                  country && country !== 'UNK' && country !== 'LOCAL'
                    ? countryToFlagEmoji(country)
                    : '';
                return (
                  <div className='d-flex align-items-center gap-2'>
                    {flag && (
                      <span style={{ fontSize: '13px' }}>{flag}</span>
                    )}
                    <span>
                      {country && country !== 'UNK' ? `${country} ` : ''}
                      {ip}
                    </span>
                  </div>
                );
              },
            },
            {
              accessor: 'action',
              label: t('login_log_col_status'),
              sort: true,
              render: (row) => (
                <Badge
                  text={
                    row.action === 'LOGIN'
                      ? t('login_log_status_success')
                      : t('login_log_status_failed')
                  }
                  color={row.action === 'LOGIN' ? 'success' : 'error'}
                  size='sm'
                />
              ),
            },
          ]}
          data={rows}
          page={page}
          onPageChange={setPage}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          paginationMode='server'
          totalItems={total}
          paginator
          serverSortKey={sortKey}
          serverSortDirection={sortDirection}
          onServerSortChange={handleServerSortChange}
          loading={loading}
          paginatorGroup={[
            { label: '10', value: 10 },
            { label: '20', value: 20 },
            { label: '50', value: 50 },
            { label: '100', value: 100 },
          ]}
        />
        {!loading && rows.length === 0 && (
          <div className='text-center p-4'>
            <p className='caption font-light'>{t('login_log_no_results')}</p>
          </div>
        )}
      </div>
    </Card>
  );
}
